import { ICONES_EVENTS } from '@modules/consolidator/constants/Text'
import { getNumber } from '@utils/Format'
import { isUsdCurrency } from '@modules/consolidator/utils/Currency/Currency'
import { MASK_BRL, MASK_USD } from '@modules/global/constants/Currencies'

export const COME_COTAS = 'Come-Cotas'
export const BONIFICACAO = 'Bonificação'

/**
 * A linha e de bonificacao? O campo dela e PERCENTUAL, entao nao passa por
 * `toMoney`. Exportado porque os dois componentes de tabela recriavam o literal
 * inline — o mesmo erro de ter dois reconhecedores do mesmo formato.
 */
export const isBonusEvent = (item) =>
  Boolean(item && item.operacao && item.operacao.includes(BONIFICACAO))

const isPercentEvent = (event) => {
  const eventNames = [event.evento || '', event.operacao || '']
  return eventNames.some(
    (eventName) =>
      eventName.includes(COME_COTAS) || eventName.includes(BONIFICACAO)
  )
}

export const getEventIconDefinition = ({
  eventItem,
  key = 'operacao',
  iconsDictionary = ICONES_EVENTS,
} = {}) => {
  const empty = { name: '', color: '', iconColor: '' }

  if (!iconsDictionary) return empty

  const operation =
    eventItem && typeof eventItem === 'object' && key
      ? eventItem[key]
      : undefined

  const iconConfig =
    (operation && iconsDictionary[operation]) || iconsDictionary.default

  if (!iconConfig) return empty

  return {
    name: iconConfig.name || '',
    color: iconConfig.color || '',
    iconColor: iconConfig.iconColor || '',
  }
}

export const stampConsolidatorEvents = ({
  map = {},
  type = 'provento',
} = {}) => {
  const isSplit = type === 'split'
  const prefix = isSplit ? 'split' : 'prov'
  const stampedMap = {}

  Object.keys(map || {}).forEach((idAtivo) => {
    const list = Array.isArray(map[idAtivo]) ? map[idAtivo] : []
    stampedMap[idAtivo] = list.map((event, index) => {
      const stamped = { ...event }
      if (isSplit) {
        stamped.split = true
        stamped.operacao =
          stamped.split_ratio > 1 ? 'Desdobramento' : 'Agrupamento'
      } else {
        stamped.provento = true
        stamped.operacao = stamped.evento || 'Proventos'
      }
      stamped.data = stamped.data_com
      stamped.id_ativo = idAtivo
      stamped._id = index
      stamped._eventUid = `${prefix}_${idAtivo}_${index}`
      return stamped
    })
  })

  return stampedMap
}

/**
 * Trecho entre parenteses do texto de valor, ou string vazia. Feito com
 * `indexOf`/`slice` de proposito: a versao com regex casava dois quantificadores
 * adjacentes e o Sonar abria Security Hotspot de backtracking (S5852), que
 * derruba o quality gate mesmo com zero issue — o benchmark
 * CP-REGEX-LOCK-BACKTRACKING ja previa isso.
 */
const maskAsUsd = (texto) => `${MASK_USD}${texto.slice(MASK_BRL.length)}`

const conversionChunk = (text) => {
  const open = text.indexOf('(')
  const close = text.indexOf(')', open + 1)
  if (open < 0 || close < 0) return ''
  const inside = text.slice(open + 1, close)
  return inside.includes(' x ') ? inside : ''
}

/**
 * O texto que a rota entregou carrega uma CONVERSAO de fato?
 *
 * E o discriminador da MOEDA da linha inteira, medido no mesmo ativo (186922,
 * cliente 25925 de homologacao) antes e depois de uma cotizacao:
 *
 *   "R$ 1,07 (US$  0,20 x R$ 5,37)"  valor_total 2152,53 / qtd 2005,491 = 1,07 -> REAIS
 *   "R$ 0,20 (- x -)"                valor_total  401,10 / qtd 2005,491 = 0,20 -> DOLAR
 *
 * Sem conversao, o `R$` do texto e rotulo default da cotizacao, nao moeda: o
 * numero (e o `valor_total` que o acompanha) esta na moeda do ATIVO.
 */
export const isConvertedValueText = (value) => {
  if (typeof value !== 'string') {
    return false
  }
  const converted = conversionChunk(value)
  // `(- x -)` TAMBEM casa o ` x ` — o placeholder e o convertido tem a mesma
  // forma. Quem separa os dois e o numero: no placeholder o primeiro pedaco e
  // `-`, que nao vira numero.
  return Boolean(converted) && /\d/.test(converted.split(' x ')[0])
}

/**
 * Valor unitario do evento NA MOEDA DO ATIVO — que e o que o arquivo de eventos
 * do ativo guarda.
 *
 * A rota de proventos entrega `valor` como texto e em DOIS formatos:
 *
 *   "R$ 183,51 (US$  33,75 x R$ 5,44)"  -> houve conversao. O `valor_total` do
 *                                          resumo esta em REAIS; a moeda do
 *                                          ativo e o numero antes do ` x `
 *   "R$ 1,31 (- x -)"                   -> nao houve conversao. O `valor_total`
 *                                          ja esta na moeda do ativo
 *
 * Medido em 2026-08-20 comparando o resumo com o arquivo de eventos do MESMO
 * ativo: no formato convertido `valor_total / quantidade` da 183,5055 (reais)
 * enquanto a fonte guarda 33,75 (dolares) — o cupom multiplicado pela cotacao.
 *
 * Limite conhecido: o trecho convertido traz 2 casas decimais, entao evento com
 * mais precisao na origem perde o resto no round-trip. Fechar isso de vez exige
 * a rota devolver o valor na moeda do ativo, nao o texto de exibicao.
 */
const valueInAssetCurrency = (event) => {
  const text = String(event.valor)
  const converted = conversionChunk(text)
  // Testa DIGITO, nao truthiness: um cupom de `US$ 0,00` e zero LEGITIMO, e o
  // teste por truthiness o mandava pro fallback e devolvia a string inteira
  // pro PUT — o mesmo 400 que esta task existe pra matar, numa borda.
  const inAssetCurrency = converted ? converted.split(' x ')[0] : ''
  if (/\d/.test(inAssetCurrency)) {
    return getNumber(inAssetCurrency)
  }
  const totalValue = getNumber(event.valor_total)
  const quantity = getNumber(event.quantidade)
  if (totalValue && quantity) {
    return totalValue / quantity
  }
  // Conta os NUMEROS do texto sem quantificador aninhado (o `\d+([.,]\d+)*`
  // que fazia isso e Security Hotspot de backtracking no Sonar). Um numero so
  // = da pra parsear; mais de um = texto composto, devolve intacto.
  const numericGroups = text.split(/[^\d.,]+/).filter((part) => /\d/.test(part))
  if (numericGroups.length !== 1) {
    return event.valor
  }
  // Parseia o GRUPO isolado, nunca o texto inteiro: os `-` de `(- x -)`
  // sobrevivem ao strip do `getNumber`, o `Number()` devolve NaN e o `|| 0`
  // engole isso num ZERO. Como o save reescreve a lista inteira do ativo,
  // esse zero cai em linha que ninguem tocou — e o `hasZeroedValues` a esconde,
  // entao a perda fica invisivel. Pior que o 400 alto que a task veio corrigir.
  return getNumber(numericGroups[0])
}

/**
 * Valor unitario de UMA linha, na moeda do ativo.
 *
 * Exportado de proposito, mesmo sem consumidor de producao: e a unidade da
 * regra que o `normalizeEventList` aplica em lote, e os casos de borda
 * (percentual, split sem valor, zero legitimo, texto nao-parseavel) sao
 * testados aqui, um a um. Testar so pelo lote esconderia qual ramo quebrou.
 */
export const resolveEventUnitValue = (event) => {
  if (!event) {
    return event
  }
  if (typeof event.valor !== 'string') {
    return event.valor
  }
  if (isPercentEvent(event)) {
    return event.valor
  }
  return valueInAssetCurrency(event)
}

/**
 * `valor_total` coerente com o `valor` que vai junto. No formato convertido o
 * total do resumo esta em reais, entao mandar o unitario em dolar com o total em
 * reais gravaria as duas colunas em moedas diferentes no mesmo registro.
 *
 * Sao DUAS derivacoes e elas nao dao o mesmo numero. Com `quantidade`, manda
 * `unitario * quantidade`; sem ela, manda `valor_total / cotacao`. Na fixture
 * medida do §10.1 isso e `33,75 x 50 = 1687,50` contra `9175,275 / 5,44 =
 * 1686,63` — 0,87 de diferenca, porque o unitario do parentese ja vem truncado
 * em 2 casas. A da QUANTIDADE manda quando ela existe: e ela que preserva
 * `valor * quantidade == valor_total` dentro do arquivo do ativo.
 */
const totalInAssetCurrency = (event, unitValue) => {
  if (!event) {
    return undefined
  }
  if (typeof event.valor !== 'string' || typeof unitValue !== 'number') {
    return event.valor_total
  }
  if (!isConvertedValueText(event.valor)) {
    return event.valor_total
  }
  const quantity = getNumber(event.quantidade)
  if (quantity) {
    return unitValue * quantity
  }
  // Sem quantidade, `valor_total` sozinho voltaria em REAIS ao lado de um
  // unitario em dolar — a incoerencia que esta funcao existe pra impedir. A
  // cotacao esta dentro do proprio parentese (`US$ 33,75 x R$ 5,44`), entao da
  // pra derivar o total sem depender da quantidade.
  //
  // `total` precisa do teste de TIPO, nao de truthiness: `getNumber` devolve o
  // argumento intacto quando ele e falsy (`Format.js`), entao `undefined / rate`
  // gravaria NaN e `null / rate` gravaria 0 numa linha que ninguem editou —
  // perda silenciosa, pior que devolver o total original.
  // `rate` zerado (`"R$ 183,51 (US$ 33,75 x -)"`, meio-placeholder) cai no
  // fallback e grava o total em reais ao lado do unitario em dolar. Esse
  // formato nao foi observado na rota; se aparecer, sai incoerente de proposito
  // — o alternativa seria inventar um numero.
  const rate = getNumber(conversionChunk(event.valor).split(' x ')[1])
  const total = getNumber(event.valor_total)
  if (rate && typeof total === 'number') {
    return total / rate
  }
  return event.valor_total
}

/**
 * Moeda de exibicao de UMA linha: a da LINHA vence a do ativo. Mora aqui porque
 * `eventos-table` e `movements-events-combined-table` implementavam a mesma
 * regra copiada, e foi divergindo entre os dois que o rotulo saiu errado.
 */
export const resolveEventAssetCurrency = ({ item, asset } = {}) =>
  (item && item.moeda) || (asset && asset.moeda) || ''

const DISPLAY_KEY_SUFFIX = '_f'
const EVENT_DISPLAY_VALUE_KEYS = Object.freeze(['valor_f', 'valor_exibicao'])

const isDisplayKey = (chave) =>
  chave.endsWith(DISPLAY_KEY_SUFFIX) || chave === 'valor_exibicao'

const displayKeysFor = (campo) =>
  campo === 'valor' ? EVENT_DISPLAY_VALUE_KEYS : [`${campo}_f`]

export const resolveEventDisplayValue = (event, campo = 'valor') => {
  if (!event || isPercentEvent(event)) {
    return ''
  }
  const chave = displayKeysFor(campo).find(
    (candidata) =>
      typeof event[candidata] === 'string' && event[candidata].trim()
  )
  return chave ? event[chave] : ''
}

const isClientOnlyKey = (chave) =>
  chave.charAt(0) === '_' || isDisplayKey(chave)

const omitClientOnlyKeys = (event) => {
  const limpo = {}
  Object.keys(event).forEach((chave) => {
    if (!isClientOnlyKey(chave)) {
      limpo[chave] = event[chave]
    }
  })
  return limpo
}

export const normalizeEventList = (lista) => {
  if (!Array.isArray(lista)) {
    return []
  }
  return lista.map((event) => {
    if (!event) {
      return event
    }
    const valor = resolveEventUnitValue(event)
    return {
      ...omitClientOnlyKeys(event),
      valor,
      valor_total: totalInAssetCurrency(event, valor),
    }
  })
}

export const zeroEventValues = (event) => {
  if (!event) {
    return event
  }
  return {
    ...event,
    valor: 0,
    valor_total: 0,
    quantidade: 0,
  }
}

export const hasZeroedValues = (event) => {
  if (!event) {
    return false
  }
  const hasZeroValor = getNumber(event.valor) === 0
  const hasZeroTotal = getNumber(event.valor_total) === 0
  const hasZeroQuantidade = getNumber(event.quantidade) === 0
  return hasZeroValor && hasZeroTotal && hasZeroQuantidade
}

/**
 * Codigo de moeda que o `toMoney` espera para UMA linha de evento.
 *
 * Vivia copiado em `eventos-table.vue` e `movements-events-combined-table.vue`.
 * Mora AQUI, e nao no `Currency.js`, porque e conceito de EVENTO: o `Currency.js`
 * e util-folha de 25+ arquivos (12 deles `asset-form-*`) e faze-lo depender do
 * modulo de eventos inverte a direcao e fica a um import de virar ciclo — que,
 * com os dois exportando arrow `const`, quebraria como TDZ em runtime, nao como
 * erro de lint.
 *
 * Convertido -> a cotizacao ja passou pra REAIS. Nao convertido (placeholder ou
 * ja numerico) -> o numero esta na moeda do ATIVO.
 */
export const resolveEventRowCurrency = ({ value, currency } = {}) => {
  if (isConvertedValueText(value)) {
    return 'real'
  }
  return isUsdCurrency({ currency }) ? 'dol' : 'real'
}

/**
 * Corrige o ROTULO de um valor que a cotizacao entregou como texto SEM ter
 * convertido: `"R$ 0,20 (- x -)"` num ativo em dolar sao 20 centavos de DOLAR
 * com rotulo de real.
 *
 * Mora AQUI, junto do `isConvertedValueText`, porque quem sabe reconhecer o
 * formato tem que ser UM so — o `CLAUDE.md` §10.1 do modulo diz isso com todas
 * as letras. A versao anterior vivia no `Currency.js` com um literal proprio
 * (`'(- x -)'`), e os dois divergiam por construcao: um decidia por DIGITO
 * dentro do parentese, o outro por igualdade de string.
 *
 * O front so corrige a EXIBICAO. O total em reais da carteira continua errado
 * enquanto a cotizacao nao converter — isso e a AZ-041b6863.
 */
export const relabelUnconvertedValue = ({ value, currency } = {}) => {
  if (typeof value !== 'string' || isConvertedValueText(value)) {
    return value
  }
  if (!isUsdCurrency({ currency }) || !conversionChunk(value)) {
    return value
  }
  const numero = value.slice(0, value.indexOf('(')).trim()
  if (!numero.startsWith(MASK_BRL)) {
    return value
  }
  return maskAsUsd(numero)
}

const swapBrlMask = ({ value, currency }) => {
  if (!isUsdCurrency({ currency }) || !value.trim().startsWith(MASK_BRL)) {
    return value
  }
  return maskAsUsd(value.trim())
}

export const resolveEventDisplayText = ({ event, campo, currency } = {}) => {
  const texto = resolveEventDisplayValue(event, campo)
  if (!texto || isConvertedValueText(texto)) {
    return texto || ''
  }
  if (conversionChunk(texto)) {
    return relabelUnconvertedValue({ value: texto, currency })
  }
  return swapBrlMask({ value: texto, currency })
}
